import { useMemo } from 'react';
import { useLocation } from 'react-router-dom';
import { findMarketplaceGroup } from '../constants/marketplace';
import { useGroupsWithState } from './groups/groupsHooks';

/**
 * Hook to detect if the current context is marketplace
 * 
 * Checks:
 * 1. If we're on the marketplace route (/marketplace)
 * 2. If the provided groupId matches the marketplace group
 * 
 * @param groupId - Optional group ID to check against marketplace group
 * @returns Object with isMarketplace boolean
 * 
 * @example
 * ```tsx
 * const { isMarketplace } = useMarketplaceDetection(groupId);
 * const buttonText = isMarketplace ? 'Post an Item' : 'Add a Comment';
 * ```
 */
export const useMarketplaceDetection = (groupId?: string) => {
  const location = useLocation();
  const { groups } = useGroupsWithState();

  const isMarketplace = useMemo(() => {
    // Check if we're on the marketplace route first (most reliable)
    const isMarketplaceRoute = 
      location.pathname === '/marketplace' || 
      location.pathname.startsWith('/marketplace/');

    if (isMarketplaceRoute) {
      return true;
    }

    // If we have a groupId, check if it matches the marketplace group
    if (groupId) {
      const marketplaceGroup = findMarketplaceGroup(groups || []);
      const isMarketplaceGroup = marketplaceGroup?.uuid === groupId;
      return isMarketplaceGroup;
    }

    // Default to false if no route match and no groupId
    return false;
  }, [location.pathname, groupId, groups]);

  return { isMarketplace };
};
